import { type DepartmentListItem, type DepartmentListQuery } from '@utazzo/shared';
import { UzStatusBadge } from '@utazzo/ui';
import React from 'react';

import { DepartmentFormDrawer } from '../components/DepartmentFormDrawer';
import { listDepartments } from '../departments.api';
import {
  useBulkDeleteDepartments,
  useBulkSetDepartmentsStatus,
  useDeleteDepartment,
  useDepartmentsList,
} from '../use-departments';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';
import { useCurrentUser } from '@/features/auth/use-auth';

const STATUS_OPTIONS = [
  { value: '', label: 'Active + inactive' },
  { value: 'active', label: 'Active only' },
  { value: 'inactive', label: 'Inactive only' },
] as const;

/** Server search only fires from this many characters. */
const MIN_SEARCH_LENGTH = 3;

const FILTERS: ReadonlyArray<DataGridFilter> = [
  {
    id: 'status',
    label: 'Status',
    options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-48',
  },
];

export const DepartmentsListPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const canDelete = me?.role === 'ADMIN';
  const deleteDepartment = useDeleteDepartment();
  const bulkDeleteMut = useBulkDeleteDepartments();
  const bulkSetStatusMut = useBulkSetDepartmentsStatus();

  const columns: ListScreenColumns<DepartmentListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<DepartmentListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        cell: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-zinc-900">{row.name}</span>
            <span className="text-xs text-zinc-500">{row.code}</span>
          </div>
        ),
      },
      {
        id: 'description',
        header: 'Description',
        cell: (row) => <span className="text-zinc-600">{row.description ?? '-'}</span>,
      },
      {
        id: 'sortOrder',
        header: 'Sort',
        sortable: true,
        width: '80px',
        cell: (row) => <span>{row.sortOrder}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => <UzStatusBadge status={row.isActive ? 'active' : 'inactive'} />,
      },
    ];
    return cols;
  };

  return (
    <ListScreen<DepartmentListItem, DepartmentListQuery>
      title="Departments"
      description="Organizational units users belong to (e.g. sales, operations, finance)."
      useList={useDepartmentsList}
      defaultSort={{ by: 'sortOrder', dir: 'asc' }}
      minSearchLength={MIN_SEARCH_LENGTH}
      toQuery={(state, base) => {
        const status = state.filters.status;
        return {
          ...base,
          ...(status === 'active' ? { isActive: true } : {}),
          ...(status === 'inactive' ? { isActive: false } : {}),
        };
      }}
      columns={columns}
      filters={FILTERS}
      getRowId={(row) => row.id}
      createButton={{ label: 'Add department' }}
      renderDrawer={({ open, mode, row, close }) => (
        <DepartmentFormDrawer
          open={open}
          onOpenChange={(o) => {
            if (!o) close();
          }}
          mode={mode}
          department={row}
        />
      )}
      rowActions={{
        edit: true,
        delete: {
          onDelete: (row) => deleteDepartment.mutateAsync(row.id),
          disabled: () => !canDelete,
          confirm: (row) => ({
            title: 'Delete this department?',
            description: `${row.name} will be soft-deleted and hidden from lists. Users already assigned to it are unaffected.`,
          }),
        },
      }}
      bulkActions={{
        delete: {
          onDelete: async (rows) => {
            await bulkDeleteMut.mutateAsync(rows.map((r) => r.id));
          },
          confirm: (rows) => ({
            title: `Delete ${rows.length} department${rows.length === 1 ? '' : 's'}?`,
            description:
              'These departments will be soft-deleted. This can be reverted only by restoring from a backup.',
          }),
        },
        setActive: {
          onSetActive: async (rows, isActive) =>
            bulkSetStatusMut.mutateAsync({ ids: rows.map((r) => r.id), isActive }),
          confirm: (rows, isActive) => ({
            title: `${isActive ? 'Activate' : 'Deactivate'} ${rows.length} department${rows.length === 1 ? '' : 's'}?`,
            description: isActive
              ? 'They will become selectable again in lead capture and reports.'
              : 'They will be hidden from new selections but remain on historical records.',
          }),
        },
      }}
      export={{
        fetchPage: (q) => listDepartments(q),
        filename: 'departments',
        csv: [
          { header: 'Code', value: (r) => r.code },
          { header: 'Name', value: (r) => r.name },
          { header: 'Description', value: (r) => r.description ?? '' },
          { header: 'Sort order', value: (r) => r.sortOrder },
          { header: 'Status', value: (r) => (r.isActive ? 'Active' : 'Inactive') },
          { header: 'Created at', value: (r) => r.createdAt },
        ],
      }}
      copy={{
        searchPlaceholder: 'Search by name or code (min 3 characters)',
        emptyTitle: 'No departments match these filters',
        emptyDescription: 'Adjust the status filter or clear the search box.',
        errorTitle: 'Failed to load departments',
        errorDescription: 'The server returned an error. Try refreshing or check your network.',
      }}
    />
  );
};
